import React, { useEffect, useState } from "react";
import { masterAPI } from "../../../Masters/dataConfig";
import { axiosPrivate } from "../../../../axios/axios";
import { Autocomplete, Box, Button, CircularProgress, Dialog, Divider, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { color } from "../../../../StyleData/Config";
import { Clear, DeleteOutline, Domain, EditOutlined, PrintOutlined } from "@mui/icons-material";
import NormalTable from "../../../../Component/NormalTable";
import CreatePart from "./CreatePart";
import { toast } from "react-toastify";
import { printReservationForm } from "../../../../Component/printWorkOrder";
import { usePrintLoader } from "../../../../Component/usePrintLoader";

const Partlist = ({ workOrderId, enableDisable, printName }) => {
  const [partList, setPartList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [partDialogue, setPartDialogue] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewDialogue, setViewDialogue] = useState(false);
  const [viewItems, setViewItems] = useState([]);
  const [viewCode, setViewCode] = useState("");
  const { runPrint, PrintLoader } = usePrintLoader();

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    setLoading(true);
    try {
      let getPartlistlink = masterAPI["partsReservation"].getByWorkOrderId;
      let response = await axiosPrivate.get(`${getPartlistlink}/${workOrderId}`);

      setPartList(Array.isArray(response.data.result) ? response.data.result : []);
    } catch (error) {
      setPartList([]);
    }
    setLoading(false);
  };

  const confirmDeletePart = (id, partsReservationId) => {
    toast(
      <Grid>
        <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#797979" }}>Are you sure you want to delete?</Typography>
        <Grid item container spacing={2}>
          <Grid item>
            <Button
              onClick={() => {
                handlePartDelete(id, partsReservationId);
                toast.dismiss();
              }}
              sx={{ color: "grey", borderColor: "green" }}
              size="small"
              variant="outlined"
            >
              Yes
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={() => toast.dismiss()}
              sx={{ color: "grey", borderColor: "red" }}
              size="small"
              variant="outlined"
            >
              No
            </Button>
          </Grid>
        </Grid>
      </Grid>,
      {
        closeButton: false,
        autoClose: false,
      }
    );
  };

  const handlePartDelete = async (id, partsReservationId) => {
    const partDelLink = masterAPI["partsReservation"].deleteParts;
    await axiosPrivate.delete(`${partDelLink}/${partsReservationId}/${id}`).then(res => {
      console.log(res);
      toast.success("Part deleted successfully");
      getData();
    }).catch(e => {
      console.log(e, "delete part error");
      toast.error("Error deleting part");
    });
  };

  const handleCodeClick = (row) => {
    setViewItems(row.partsReservationItemsList || []);
    setViewCode(row.code);
    setViewDialogue(true);
  };

  const itemColumns = [
    {
      Header: "Part Code",
      accessor: "partCode",
    },
    {
      Header: "Part Name",
      accessor: "partName",
    },
    {
      Header: "Stock Room",
      accessor: "stockRoomName",
    },
    {
      Header: "Quantity",
      accessor: "quantity",
    },
  ];

  const partsColumns = [
    {
      Header: "Code",
      accessor: "code",
      Cell: ({ row }) => (
        <Typography
          sx={{
            color: "#0d6efd",
            fontSize: "inherit",
            fontWeight: 600,
            cursor: "pointer",
          }}
          onClick={() => handleCodeClick(row.original)}
        >
          {row.original.code}
        </Typography>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
    },
    {
      Header: "Remarks",
      accessor: "comments",
      Cell: ({ row }) =>
        row.original.status?.toUpperCase() === "REJT"
          ? row.original.comments || "---"
          : "---",
    },
    {
      Header: "Actions",
      Cell: ({ row }) => {
        const editStatus = row.original.status?.toUpperCase();
        const editDisabled =
          editStatus !== "PPRAPPR" &&
          editStatus !== "REJT" &&
          editStatus !== "OPEN";
        return (
          <Box sx={{ whiteSpace: "nowrap" }}>
            <IconButton
              disabled={editDisabled}
              onClick={() => {
                setEditData(row.original);
                setPartDialogue(true);
              }}
            >
              <EditOutlined
                sx={{
                  fontSize: color.masterEditDelButton,
                  color: editDisabled ? "default" : color.masterEditDelButtonColor,
                }}
              />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handlePartClose = () => {
    setPartDialogue(false);
    setEditData(null);
    getData();
  };

  return (
    <>
      {loading ? (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}><CircularProgress /></Box>
      ) : (
        <Grid container gap={"1rem"}>
          <Grid item xs={12} sx={{}}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Tooltip title="Print Reservation">
                <span>
                  <IconButton
                    disabled={partList.length === 0}
                    onClick={() =>
                      runPrint(printReservationForm, workOrderId, printName)
                    }
                  >
                    <PrintOutlined sx={{ fontSize: "1.7rem" }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <NormalTable columns={partsColumns} data={partList} />
          </Grid>
        </Grid>
      )}
      <Dialog
        open={partDialogue}
        onClose={handlePartClose}
        fullWidth
        maxWidth={"lg"}
      >
        <CreatePart handleClose={handlePartClose} workOrderId={workOrderId} editData={editData} />
      </Dialog>
      <Dialog
        open={viewDialogue}
        onClose={() => setViewDialogue(false)}
        fullWidth
        maxWidth={"md"}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "0.5rem" }}>
            <Typography sx={{ color: color.masterDialogTitleColor, fontSize: color.masterDialogFontSize }}>
              Part List {viewCode ? `- ${viewCode}` : ""}
            </Typography>
            <IconButton onClick={() => setViewDialogue(false)}>
              <Clear />
            </IconButton>
          </Box>
          <Divider sx={{ border: "1px solid rgb(0 0 0 / 17%)", mb: "15px" }} />
          <NormalTable columns={itemColumns} data={viewItems} />
        </Box>
      </Dialog>
      {PrintLoader}
    </>
  );
};

export default Partlist;

